import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';
import { useNostrNavigate } from '@/hooks/useNostrNavigate';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import type { NostrEvent } from '@nostrify/nostrify';

interface EmbeddedNoteProps {
  eventId: string;
  relays?: string[];
  authorHint?: string;
  className?: string;
}

function QuotedNoteContent({ event, onClick }: { event: NostrEvent; onClick: (e: React.MouseEvent) => void }) {
  const author = useAuthor(event.pubkey);
  const meta = author.data?.metadata;
  const displayName = meta?.name ?? meta?.display_name ?? genUserName(event.pubkey);

  // Truncate long content for the preview card
  const preview = event.content.replace(/https?:\/\/\S+/g, '').trim();
  const truncated = preview.length > 200 ? preview.slice(0, 200) + '…' : preview;

  return (
    <button
      onClick={onClick}
      className="w-full text-left"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Avatar className="w-5 h-5 shrink-0">
          <AvatarImage src={meta?.picture} alt={displayName} />
          <AvatarFallback className="text-[8px]">{displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="text-xs font-semibold truncate">{displayName}</span>
        {meta?.nip05 && (
          <span className="text-[10px] text-muted-foreground truncate">{meta.nip05.replace(/^_@/, '')}</span>
        )}
      </div>
      {truncated ? (
        <p className="text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap break-words">{truncated}</p>
      ) : (
        <p className="text-xs text-muted-foreground italic">[ media / attachment ]</p>
      )}
    </button>
  );
}

export function EmbeddedNote({ eventId, relays, authorHint, className }: EmbeddedNoteProps) {
  const { nostr } = useNostr();
  const { openEventById } = useNostrNavigate();

  const { data: event, isLoading } = useQuery({
    queryKey: ['quoted-note', eventId],
    queryFn: async ({ signal }) => {
      const filter = authorHint
        ? { ids: [eventId], authors: [authorHint], limit: 1 }
        : { ids: [eventId], limit: 1 };
      const results = await nostr.query([filter], { signal });
      return results[0] ?? null;
    },
    staleTime: 300000,
  });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openEventById(eventId, authorHint, e);
  };

  return (
    <div
      className={cn(
        'border border-border/60 rounded-lg px-3 py-2.5 my-1 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer',
        className,
      )}
      onClick={handleClick}
    >
      {isLoading ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Skeleton className="w-5 h-5 rounded-full" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/4" />
        </div>
      ) : event ? (
        <QuotedNoteContent event={event} onClick={handleClick} />
      ) : (
        // Fallback: event not found on connected relays
        <div className="text-xs text-muted-foreground font-mono">
          {nip19.neventEncode({ id: eventId, ...(authorHint ? { author: authorHint } : {}), ...(relays?.length ? { relays } : {}) }).slice(0, 28)}…
        </div>
      )}
    </div>
  );
}
